import { useState, useEffect, useCallback } from 'react';
import { Play, Star, ChevronRight, Flame } from 'lucide-react';
import { getTeamFlag } from '../../utils/teamFlags';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Streak helpers (localStorage only) ──────────────────────────
const STREAK_KEY = 'motd_streak';
const LAST_DATE_KEY = 'motd_last_date';
const PREV_STREAK_KEY = 'motd_prev_streak';
const COMEBACK_SHOWN_KEY = 'motd_comeback_shown'; // sessionStorage

const COMEBACK_MESSAGES = [
  'Welcome back',
  'Good to see you again',
  'Match of the Day is ready',
];

function todayStr() {
  return new Date().toISOString().slice(0, 10); // "2026-03-25"
}

function yesterdayStr() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

function readStreak() {
  const last = localStorage.getItem(LAST_DATE_KEY);
  const streak = parseInt(localStorage.getItem(STREAK_KEY) || '0', 10);
  if (!last) return 0;
  if (last === todayStr()) return streak;
  if (last === yesterdayStr()) return streak;
  // Gap detected — save previous streak before resetting
  if (streak >= 2) localStorage.setItem(PREV_STREAK_KEY, String(streak));
  return 0;
}

function recordStreakInteraction() {
  const last = localStorage.getItem(LAST_DATE_KEY);
  const today = todayStr();
  if (last === today) return null; // already recorded today

  const prev = readStreak();
  const next = (last === yesterdayStr()) ? prev + 1 : 1;
  localStorage.setItem(STREAK_KEY, String(next));
  localStorage.setItem(LAST_DATE_KEY, today);
  return next; // returns new streak count (for micro-feedback)
}

// ── Component ───────────────────────────────────────────────────
export function MatchOfTheDayCard({ navigate }) {
  const [data, setData] = useState(null);
  const [streak, setStreak] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [comeback, setComeback] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API}/motd`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    })
      .then(r => r.json())
      .then(d => { if (d.match) setData(d); })
      .catch(() => {});

    const s = readStreak();
    setStreak(s);

    // Comeback detection: had a streak >= 2, now reset, not yet shown this session
    if (s === 0) {
      const prev = parseInt(localStorage.getItem(PREV_STREAK_KEY) || '0', 10);
      const alreadyShown = sessionStorage.getItem(COMEBACK_SHOWN_KEY);
      if (prev >= 2 && !alreadyShown) {
        const msg = COMEBACK_MESSAGES[Math.floor(Math.random() * COMEBACK_MESSAGES.length)];
        setComeback(msg);
        sessionStorage.setItem(COMEBACK_SHOWN_KEY, '1');
      }
      localStorage.removeItem(PREV_STREAK_KEY);
    }
  }, []);

  // Clear feedback after 3s, comeback after 1.5s
  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => setFeedback(null), 3000);
    return () => clearTimeout(t);
  }, [feedback]);

  useEffect(() => {
    if (!comeback) return;
    const t = setTimeout(() => setComeback(null), 1500);
    return () => clearTimeout(t);
  }, [comeback]);

  const handleInteraction = useCallback(() => {
    if (!data) return;
    const newStreak = recordStreakInteraction();
    if (newStreak !== null) {
      setStreak(newStreak);
      if (newStreak >= 2) {
        setFeedback(`${newStreak}-day streak`);
        // Brief pause so user sees the feedback before navigating
        setTimeout(() => navigate(`/matchday/${data.match.id}`), 600);
        return;
      }
    }
    navigate(`/matchday/${data.match.id}`);
  }, [data, navigate]);

  if (!data) return null;

  const { match, status, status_label } = data;
  const homeFlag = getTeamFlag(match.home);
  const awayFlag = getTeamFlag(match.away);
  const isLive = status === 'live';

  return (
    <section className="py-4 sm:py-5" data-testid="match-of-the-day">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div
          onClick={handleInteraction}
          className="relative aspect-[16/9] max-h-[420px] rounded-2xl overflow-hidden cursor-pointer group"
          data-testid="motd-card"
        >
          {/* Background */}
          <img
            src={match.thumbnail_url}
            alt={match.teams}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" />

          {/* Top-left: MOTD label + live badge + streak */}
          <div className="absolute top-4 left-4 sm:top-6 sm:left-6 flex items-center gap-2" data-testid="motd-label">
            <div className="flex items-center gap-1.5 px-3 py-1 bg-amber-500 rounded-full">
              <Star className="w-3 h-3 text-black" fill="currentColor" />
              <span className="text-black text-[10px] sm:text-xs font-black uppercase tracking-wider">Match of the Day</span>
            </div>
            {isLive && (
              <span className="px-2 py-1 bg-red-600 text-white text-[10px] font-bold rounded-full animate-pulse">LIVE</span>
            )}
            {streak >= 2 && (
              <span
                className="flex items-center gap-1 px-2 py-1 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full"
                data-testid="motd-streak-badge"
              >
                <Flame className="w-3 h-3 text-amber-400" />
                <span className="text-white/80 text-[10px] font-semibold">{streak}-day streak</span>
              </span>
            )}
          </div>

          {/* Top-right: status label */}
          {status_label && (
            <div className="absolute top-4 right-4 sm:top-6 sm:right-6">
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                isLive ? 'bg-red-600 text-white' : 'bg-white/15 backdrop-blur-sm text-white border border-white/20'
              }`} data-testid="motd-status">
                {status_label}
              </span>
            </div>
          )}

          {/* Micro-feedback — appears briefly after interaction */}
          {feedback && (
            <div
              className="absolute top-16 left-4 sm:top-[72px] sm:left-6 flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/90 backdrop-blur-sm rounded-full animate-in fade-in slide-in-from-top-2 duration-300"
              data-testid="motd-streak-feedback"
            >
              <Flame className="w-3.5 h-3.5 text-black" />
              <span className="text-black text-xs font-bold">You're on a {feedback}</span>
            </div>
          )}

          {/* Bottom content */}
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 lg:p-8">
            {/* Competition */}
            {match.competition && (
              <span className="text-emerald-400 text-[10px] sm:text-xs font-bold uppercase tracking-wider mb-2 block" data-testid="motd-competition">
                {match.competition}
              </span>
            )}

            {/* Teams display */}
            <div className="flex items-center gap-3 sm:gap-5 mb-3" data-testid="motd-teams">
              <div className="flex items-center gap-2 sm:gap-3">
                {homeFlag ? (
                  <span className="text-3xl sm:text-4xl lg:text-5xl">{homeFlag}</span>
                ) : (
                  <span className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white font-bold text-xs sm:text-sm">
                    {match.home.slice(0, 3).toUpperCase()}
                  </span>
                )}
                <span className="text-white font-bold text-xl sm:text-2xl lg:text-3xl">{match.home}</span>
              </div>
              <span className="text-white/40 text-sm sm:text-lg font-medium uppercase">vs</span>
              <div className="flex items-center gap-2 sm:gap-3">
                {awayFlag ? (
                  <span className="text-3xl sm:text-4xl lg:text-5xl">{awayFlag}</span>
                ) : (
                  <span className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white font-bold text-xs sm:text-sm">
                    {match.away.slice(0, 3).toUpperCase()}
                  </span>
                )}
                <span className="text-white font-bold text-xl sm:text-2xl lg:text-3xl">{match.away}</span>
              </div>
            </div>

            {/* Venue + followed badge */}
            <div className="flex items-center gap-3 mb-4">
              {match.venue && (
                <span className="text-white/50 text-xs sm:text-sm">{match.venue}</span>
              )}
              {match.is_followed && (
                <span className="px-2 py-0.5 bg-emerald-500/15 border border-emerald-500/25 rounded-full text-emerald-400 text-[9px] font-bold" data-testid="motd-followed">
                  Your team
                </span>
              )}
            </div>

            {/* CTA + comeback message */}
            <div className="flex items-center gap-4">
              <button
                className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-bold text-sm uppercase tracking-wider transition-all group-hover:scale-105 ${
                  isLive
                    ? 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/30'
                    : 'bg-amber-500 hover:bg-amber-600 text-black shadow-lg shadow-amber-500/20'
                }`}
                data-testid="motd-cta"
              >
                {isLive ? <Play className="w-4 h-4" fill="currentColor" /> : <ChevronRight className="w-4 h-4" />}
                {isLive ? 'Watch Live' : 'View Match'}
              </button>
              {comeback && (
                <span
                  className="text-white/50 text-xs font-medium animate-in fade-in duration-500"
                  style={{ animation: 'fadeInOut 1.5s ease-in-out forwards' }}
                  data-testid="motd-comeback"
                >
                  {comeback}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
